import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Member {
  memberId: string;
  lineUserId: string;
  email: string | null;
  name: string | null;
  displayName: string | null;
  pictureUrl: string | null;
  phone: string | null;
  defaultInvoiceType: string | null;
  defaultMobileBarcode: string | null;
  lineAccessToken?: string; // LINE access token for authenticated API calls
  expiresAt: number;
}

interface AuthContextType {
  member: Member | null;
  isLoading: boolean;
  login: (next?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateMemberProfile: (data: Partial<Pick<Member, 'name' | 'phone' | 'email' | 'defaultInvoiceType' | 'defaultMobileBarcode'>>) => Promise<void>;
  setMemberFromSession: (sessionData: Member) => void;
  exchangeLoginCode: (code: string) => Promise<Member>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const MEMBER_SESSION_KEY = 'member_session';
const LINE_LOGIN_STATE_KEY = 'line_login_state';

// S2: session lives in sessionStorage (cleared on tab close). We keep a
// one-time migration path from the legacy localStorage entry to avoid forcing
// logout for existing users during rollout.
function readStoredSession(): Member | null {
  try {
    // 1. sessionStorage (new home)
    const fromSession = sessionStorage.getItem(MEMBER_SESSION_KEY);
    if (fromSession) {
      const parsed = JSON.parse(fromSession) as Member;
      if (parsed.expiresAt > Date.now()) return parsed;
      sessionStorage.removeItem(MEMBER_SESSION_KEY);
      return null;
    }

    // 2. legacy localStorage — migrate if still valid, then delete.
    const fromLocal = localStorage.getItem(MEMBER_SESSION_KEY);
    if (fromLocal) {
      // Regardless of validity, clear the legacy key so token stops sitting in localStorage.
      localStorage.removeItem(MEMBER_SESSION_KEY);
      try {
        const parsed = JSON.parse(fromLocal) as Member;
        if (parsed.expiresAt > Date.now()) {
          sessionStorage.setItem(MEMBER_SESSION_KEY, JSON.stringify(parsed));
          return parsed;
        }
      } catch {
        // fall through
      }
    }

    // 3. Clean up legacy oauth-state key in localStorage too.
    if (localStorage.getItem(LINE_LOGIN_STATE_KEY)) {
      localStorage.removeItem(LINE_LOGIN_STATE_KEY);
    }
    return null;
  } catch (error) {
    console.error('[AuthContext] Failed to load session:', error);
    try { sessionStorage.removeItem(MEMBER_SESSION_KEY); } catch { /* noop */ }
    try { localStorage.removeItem(MEMBER_SESSION_KEY); } catch { /* noop */ }
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const s = readStoredSession();
    if (s) setMember(s);
    setIsLoading(false);
  }, []);

  // Initiate LINE Login
  const login = async (next: string = '/') => {
    console.log('[AuthContext] Starting LINE login...');
    try {
      const response = await supabase.functions.invoke('line-login', {
        body: {
          frontendUrl: window.location.origin,
          next,
        },
      });

      if (response.error) {
        console.error('[AuthContext] Edge function error:', response.error);
        throw new Error(response.error.message || 'Edge function error');
      }

      const data = response.data;

      if (data?.success && data?.authUrl) {
        // Save OAuth state in sessionStorage (was localStorage). Cleared on tab close.
        sessionStorage.setItem(LINE_LOGIN_STATE_KEY, data.state);
        window.location.href = data.authUrl;
      } else {
        console.error('[AuthContext] Invalid response data:', data);
        throw new Error(data?.error || 'Failed to get auth URL');
      }
    } catch (error) {
      console.error('[AuthContext] Login failed:', error);
      throw error;
    }
  };

  // Logout — revoke LINE token server-side, then clear local state (fail-open).
  const logout = async () => {
    const token = member?.lineAccessToken;
    if (token) {
      try {
        await supabase.functions.invoke('line-logout', {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.warn('[AuthContext] line-logout call failed (continuing local clear):', err);
      }
    }
    try { sessionStorage.removeItem(MEMBER_SESSION_KEY); } catch { /* noop */ }
    try { sessionStorage.removeItem(LINE_LOGIN_STATE_KEY); } catch { /* noop */ }
    try { localStorage.removeItem(MEMBER_SESSION_KEY); } catch { /* noop */ }
    try { localStorage.removeItem(LINE_LOGIN_STATE_KEY); } catch { /* noop */ }
    // PostHog: clear identified user so subsequent events belong to a fresh anon.
    try {
      const { reset } = await import('@/lib/posthog');
      reset();
    } catch { /* noop */ }
    setMember(null);
  };

  const setMemberFromSession = (sessionData: Member) => {
    setMember(sessionData);
    try {
      sessionStorage.setItem(MEMBER_SESSION_KEY, JSON.stringify(sessionData));
    } catch (err) {
      console.error('[AuthContext] Failed to persist session:', err);
    }
  };

  // Exchange a one-time login code (from ?code=... in the URL) for a session payload.
  const exchangeLoginCode = async (code: string): Promise<Member> => {
    const { data, error } = await supabase.functions.invoke('exchange-login-code', {
      body: { code },
    });
    if (error) throw error;
    if (!data?.success || !data?.session) {
      throw new Error(data?.error || 'exchange_failed');
    }
    const session = data.session as Member;
    setMemberFromSession(session);
    return session;
  };

  const updateMemberProfile = async (data: Partial<Pick<Member, 'name' | 'phone' | 'email' | 'defaultInvoiceType' | 'defaultMobileBarcode'>>) => {
    if (!member) throw new Error('Not logged in');

    try {
      const { data: result, error } = await supabase.functions.invoke('update-member', {
        body: {
          name: data.name,
          phone: data.phone,
          email: data.email,
          defaultInvoiceType: data.defaultInvoiceType,
          defaultMobileBarcode: data.defaultMobileBarcode,
        },
        headers: member.lineAccessToken
          ? { Authorization: `Bearer ${member.lineAccessToken}` }
          : undefined,
      });

      if (error) throw error;

      if (result?.success && result?.member) {
        const updatedMember: Member = {
          ...member,
          name: result.member.name,
          phone: result.member.phone,
          email: result.member.email,
          defaultInvoiceType: result.member.defaultInvoiceType,
          defaultMobileBarcode: result.member.defaultMobileBarcode,
        };
        setMember(updatedMember);
        try {
          sessionStorage.setItem(MEMBER_SESSION_KEY, JSON.stringify(updatedMember));
        } catch (err) {
          console.error('[AuthContext] Failed to persist updated session:', err);
        }
      }
    } catch (error) {
      console.error('[AuthContext] Update profile failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      member,
      isLoading,
      login,
      logout,
      updateMemberProfile,
      setMemberFromSession,
      exchangeLoginCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
